import './style/editSalesProduct.scss';

import React, {MouseEventHandler, PropsWithChildren, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {BolmateControlColor} from '../../../../util/types';

import {BolmateButton} from '../../../controls';

import {AwesomeIcon} from '@dvrd/dvr-controls';

import ProductCostsV2 from '../../../../models/productCostsV2Model';

import classNames from 'classnames';

import {DUTCH_DATE_FORMAT} from '../../../../util/constants';

import {formatCurrency, formatLVBSize} from '../../../../util/utils';

import BolProduct from '../../../../models/bolProductModel';

import {AppContext} from '../../../misc/appContext';

import CountryFlag from '../../../misc/countryFlag';

import {DefaultVAT, translateVatValue} from '../../../../util/vatUtil';

import BolmateToggleButton from '../../../controls/button/bolmateToggleButton';

interface Props {
    onClose: MouseEventHandler;
    onEditCosts: (costs: ProductCostsV2) => MouseEventHandler;
    onDeleteCosts: (costs: ProductCostsV2) => MouseEventHandler;
    onAddCosts: MouseEventHandler;
    onEditVAT: MouseEventHandler;
    onChangeVisibility: (value: boolean) => void;
    productCostsList: Array<ProductCostsV2>;
    loadingCosts: boolean;
    loadingProduct: boolean;
    bolProduct: BolProduct | null;
}

type SectionKey = 'productCosts' | 'vat' | 'visibility';

interface SectionProps {
    onToggleSection: (sectionKey: SectionKey) => MouseEventHandler;
    sectionKey: SectionKey;
    openSections: Set<SectionKey>;
}

function EditSection(props: PropsWithChildren<SectionProps>) {
    const {t} = useTranslation();
    const {onToggleSection, sectionKey, openSections, children} = props;
    const isOpen = useMemo(() => openSections.has(sectionKey), [openSections, sectionKey]);
    const sectionRef = useRef<HTMLDivElement>(null);
    const overflowTimeout = useRef<number | undefined>(undefined);
    const title = {
        productCosts: t('salesDashboardV2.productDetails.edit.productCosts', 'Productkosten'),
        vat: t('salesDashboardV2.productDetails.edit.vat', 'BTW'),
        visibility: t('salesDashboardV2.productDetails.edit.visibility', 'Zichtbaarheid')
    }[sectionKey];

    function setDelayedOverflow() {
        if (isOpen)
            overflowTimeout.current = window.setTimeout(() => {
                sectionRef.current?.style.setProperty('overflow-y', 'auto');
            }, 200);
        else sectionRef.current?.style.setProperty('overflow-y', 'hidden');
    }

    function clearTimeout() {
        window.clearTimeout(overflowTimeout.current);
        overflowTimeout.current = undefined;
    }

    useEffect(() => {
        setDelayedOverflow();
        return function () {
            clearTimeout();
        };
    }, [isOpen]);

    return (
        <section className={classNames('edit-sales-product-section', isOpen && 'open')}>
            <div className='section-header'>
                <label className='section-label' onClick={onToggleSection(sectionKey)}>
                    {title}
                </label>
                <AwesomeIcon name={isOpen ? 'chevron-up' : 'chevron-down'} className='chev-icon' />
            </div>
            <div className='section-content' ref={sectionRef}>
                {children}
            </div>
        </section>
    );
}

export default function EditSalesProduct(props: Props) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const {
        onClose,
        onEditCosts,
        onAddCosts,
        onDeleteCosts,
        onEditVAT,
        productCostsList,
        bolProduct,
        onChangeVisibility
    } = props;
    const [openSections, setOpenSections] = useState<Set<SectionKey>>(new Set(['productCosts', 'vat', 'visibility']));

    function onToggleSection(sectionKey: SectionKey) {
        return function () {
            const _openSections = new Set(openSections);
            if (_openSections.has(sectionKey)) _openSections.delete(sectionKey);
            else _openSections.add(sectionKey);
            setOpenSections(_openSections);
        };
    }

    function renderCosts() {
        return (
            <EditSection onToggleSection={onToggleSection} sectionKey='productCosts' openSections={openSections}>
                <div className='product-costs-container'>
                    <div className='row default-table-row head'>
                        <label>{t('salesDashboardV2.productDetails.edit.validFrom', 'Geldig vanaf')}</label>
                        <label>{t('salesDashboardV2.productDetails.edit.fromProducts', 'Vanaf producten')}</label>
                        <label>{t('salesDashboardV2.productDetails.edit.costPrice', 'Kostprijs')}</label>
                        <label>{t('salesDashboardV2.productDetails.edit.lvbSize', 'LVB formaat')}</label>
                        <div />
                    </div>
                    {productCostsList.map(renderProductCosts)}
                    {!productCostsList.length && (
                        <div className='row default-table-row empty'>
                            <label className='empty-label'>
                                {t(
                                    'salesDashboardV2.productDetails.edit.noCosts',
                                    '- Je hebt nog geen productkosten ingesteld -'
                                )}
                            </label>
                        </div>
                    )}
                </div>
                <BolmateButton
                    onClick={onAddCosts}
                    label={t('salesDashboardV2.productDetails.edit.addProductCosts', 'Productkosten toevoegen')}
                    ornaments={{element: <AwesomeIcon name='plus' />}}
                    color='green'
                    className='add-costs-btn'
                />
            </EditSection>
        );
    }

    function renderProductCosts(costs: ProductCostsV2) {
        return (
            <div key={costs.id} className='row default-table-row' onClick={onEditCosts(costs)}>
                <label>{costs.startDateAsDate.format(DUTCH_DATE_FORMAT)}</label>
                <label>{costs.minQuantity}</label>
                <label>{formatCurrency(costs.costPrice)}</label>
                <label>{formatLVBSize(costs.lvbSize)}</label>
                <AwesomeIcon name='trash-alt' onClick={onDeleteCosts(costs)} className='btn-delete' />
            </div>
        );
    }

    function renderVAT() {
        if (!bolProduct || !account) return null;
        return (
            <EditSection onToggleSection={onToggleSection} sectionKey='vat' openSections={openSections}>
                <div className='vats-container'>
                    <div className='current-vat-values'>
                        {renderVATCountry('NL', t('salesDashboardV2.productDetails.edit.netherlands', 'Nederland'))}
                        {renderVATCountry('BE', t('salesDashboardV2.productDetails.edit.belgium', 'België'))}
                        {renderVATCountry('DE', t('salesDashboardV2.productDetails.edit.germany', 'Duitsland'))}
                    </div>
                    <BolmateButton
                        onClick={onEditVAT}
                        label={t('salesDashboardV2.productDetails.edit.change', 'Wijzigen')}
                        ornaments={{element: <AwesomeIcon name='edit' />}}
                    />
                </div>
            </EditSection>
        );
    }

    function renderVATCountry(countryCode: string, country: string) {
        const {defaultVATs} = account!.settings;
        const vatValue = bolProduct!.getCountryVat(countryCode) ?? defaultVATs[countryCode] ?? DefaultVAT(21);
        return (
            <div className='vat-country'>
                <CountryFlag countryCode={countryCode} />
                <span className='vat-value'>
                    {country}: {translateVatValue(vatValue)}
                </span>
            </div>
        );
    }

    function renderVisibility() {
        const icon = bolProduct?.hiddenByUser ? 'eye' : 'eye-slash';
        const label = bolProduct?.hiddenByUser
            ? t('salesDashboardV2.productDetails.edit.showProduct', 'Product tonen')
            : t('salesDashboardV2.productDetails.edit.hideProduct', 'Product verbergen');
        const color: BolmateControlColor = bolProduct?.hiddenByUser ? 'green' : 'red';
        return (
            <EditSection onToggleSection={onToggleSection} sectionKey='visibility' openSections={openSections}>
                <BolmateToggleButton
                    label={label}
                    active={true}
                    onToggle={onChangeVisibility}
                    ornaments={{element: <AwesomeIcon name={icon} prefix='far' />}}
                    color={color}
                    className='visibility-toggle'
                />
                <span className='visibility-info'>
                    {t(
                        'salesDashboardV2.productDetails.edit.visibilityInfo',
                        'Dit verbergt/toont het product op het hele platform'
                    )}
                </span>
            </EditSection>
        );
    }

    return (
        <div className='edit-sales-product'>
            <div className='edit-sales-product-content'>
                {renderCosts()}
                {renderVAT()}
                {renderVisibility()}
            </div>
            <div className='footer-container'>
                <BolmateButton
                    onClick={onClose}
                    label={t('salesDashboardV2.productDetails.edit.back', 'Terug')}
                    ornaments={{element: <AwesomeIcon name='arrow-left' />}}
                />
            </div>
        </div>
    );
}
